import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    CircularProgress, 
    TablePagination,
    IconButton,
    Collapse,
    Divider
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { usePlanosVelesApi } from '../../../infra/planos-veles';
import { TipoPlano, TipoPlanoLabel } from '../../../application/models/IPlano';

const tipoPlanoLabelMap: Record<TipoPlano, string> = {
  [TipoPlano.PrePago]: TipoPlanoLabel.PrePago,
  [TipoPlano.PosPago]: TipoPlanoLabel.PosPago,
  [TipoPlano.FeeMensal]: TipoPlanoLabel.FeeMensal,
  [TipoPlano.POC]: TipoPlanoLabel.POC,
};

interface HistoricoPlanosTabProps {
  IdCliente: number;
}

const Row = (props: { plano: any }) => {
    const { plano } = props;
    const [open, setOpen] = useState(false);

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', { 
            style: 'currency', 
            currency: 'BRL',
            minimumFractionDigits: 4,
            maximumFractionDigits: 4
        }).format(value);
    };

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell width="50px">
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    {tipoPlanoLabelMap[plano.IdPlano as TipoPlano] || plano.TipoPlano}
                </TableCell>
                <TableCell align="left">{formatDate(plano.DataInclusao)}</TableCell>
                <TableCell align="left">
                    <Box 
                        component="span" 
                        sx={{ 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1, 
                            bgcolor: plano.StatusAtivo ? 'success.light' : 'grey.300',
                            color: plano.StatusAtivo ? 'success.contrastText' : 'text.primary',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {plano.StatusAtivo ? 'ATIVO' : 'INATIVO'}
                    </Box>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2, bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle2" gutterBottom component="div" color="primary" sx={{ fontWeight: 'bold' }}>
                                Detalhes da Configuração
                            </Typography>
                            
                            {plano.IdPlano === TipoPlano.PrePago && plano.PrePago && (
                                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Quantidade</Typography>
                                        <Typography variant="body2">{plano.PrePago.Quantidade.toLocaleString()}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Valor Total</Typography>
                                        <Typography variant="body2">{formatCurrency(plano.PrePago.ValorTotal)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Valor Unitário</Typography>
                                        <Typography variant="body2">{formatCurrency(plano.PrePago.ValorUnitario)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Expiração</Typography>
                                        <Typography variant="body2">{formatDate(plano.PrePago.DataExpiracao)}</Typography>
                                    </Box>
                                </Box>
                            )}

                            {plano.IdPlano === TipoPlano.POC && plano.POC && (
                                <Box sx={{ display: 'flex', gap: 4 }}>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Quantidade</Typography>
                                        <Typography variant="body2">{plano.POC.Quantidade.toLocaleString()}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Expiração</Typography>
                                        <Typography variant="body2">{formatDate(plano.POC.DataExpiracao)}</Typography>
                                    </Box>
                                </Box>
                            )}

                            {plano.IdPlano === TipoPlano.FeeMensal && plano.Fee && (
                                <Box>
                                    <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary">Range Fixo</Typography>
                                            <Typography variant="body2">{plano.Fee.Min.toLocaleString()} a {plano.Fee.Max.toLocaleString()}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary">Mensalidade</Typography>
                                            <Typography variant="body2">{formatCurrency(plano.Fee.ValorMensal)}</Typography>
                                        </Box>
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>Excedentes:</Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Mínimo</TableCell>
                                                <TableCell>Máximo</TableCell>
                                                <TableCell>Valor Unitário</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {plano.Fee.Excedentes.map((exc: any, i: number) => (
                                                <TableRow key={i}>
                                                    <TableCell>{exc.Min.toLocaleString()}</TableCell>
                                                    <TableCell>{exc.Max >= 999999999 ? '999.999.999' : exc.Max.toLocaleString()}</TableCell>
                                                    <TableCell>{formatCurrency(exc.ValorUnitario)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}

                            {plano.IdPlano === TipoPlano.PosPago && plano.PosPago && (
                                <Box>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Mínimo</TableCell>
                                                <TableCell>Máximo</TableCell>
                                                <TableCell>Valor Unitário</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {plano.PosPago.map((pos: any, i: number) => (
                                                <TableRow key={i}>
                                                    <TableCell>{pos.Min.toLocaleString()}</TableCell>
                                                    <TableCell>{pos.Max >= 999999999 ? '999.999.999' : pos.Max.toLocaleString()}</TableCell>
                                                    <TableCell>{formatCurrency(pos.ValorUnitario)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

export const HistoricoPlanosTab: React.FC<HistoricoPlanosTabProps> = ({ IdCliente }) => {
  const [historico, setHistorico] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const { obterHistoricoPlanosApi } = usePlanosVelesApi();

  useEffect(() => {
    const fetchHistorico = async () => {
      if (!IdCliente || IdCliente === 0) return;
      setLoading(true);
      try {
        const data = await obterHistoricoPlanosApi({ IdCliente });
        setHistorico(data || []);
      } catch (err) {
        // Silencioso
      } finally {
        setLoading(false);
      }
    };
    fetchHistorico();
  }, [IdCliente, obterHistoricoPlanosApi]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const startIndex = page * rowsPerPage;
  const currentHistorico = historico.slice(startIndex, startIndex + rowsPerPage);

  return (
    <Box p={2}>
      <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
        Histórico de Contratações
      </Typography>
      
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>Tipo de Plano</TableCell>
              <TableCell align="left" sx={{ fontWeight: 'bold' }}>Data de Cadastro</TableCell>
              <TableCell align="left" sx={{ fontWeight: 'bold' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={24} sx={{ my: 2 }} />
                </TableCell>
              </TableRow>
            ) : historico.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  Nenhum plano histórico encontrado para este cliente.
                </TableCell>
              </TableRow>
            ) : (
              currentHistorico.map((plano) => (
                <Row key={plano.IdPlanoCliente} plano={plano} />
              ))
            )}
          </TableBody>
        </Table>
        {!loading && historico.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={historico.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais que ${to}`}`}
          />
        )}
      </TableContainer>
    </Box>
  );
};
